import React, {useMemo, useState, useCallback, useEffect} from 'react';
import styles from './NavBar.module.scss';
import {useAuth} from '../../../store/auth';
import {getUrlRoute, MRAppRoutes} from '../../router/routes';
import {useDispatch} from 'react-redux';
import {fullNameWestern} from '../../../store/user/utils';
import {useAboutDialog} from '../../core/AboutDialog/AboutDialog';
import {useSettings} from '../../../store/appData';
import {messageAdd, MessageTypes} from '../../../store/message';
import {useNavigate} from 'react-router-dom';
import {
  deleteNotificationAction,
  useNotifications,
  requestLatestNotifications,
  userNotificationsSelector,
  resetLatestNotificationFiled,
} from '../../../store/notifications';
import {useSelector} from '../../../store/rootReducer';
import {canCreateModel, canCreateExternalUser, isAdmin} from '../../../permissions/usePermissions';
import {NotificationDTO} from 'hemwb-api';
import {TO_LOCALE_DATE_STRING_OPTIONS} from '../../core/TableWithSortAndPagination';
import 'driver.js/dist/driver.css';
import {useLiveNotificationsUpdates} from '../../../websocket/useLiveNotificationsUpdates';
import {Header, Logo} from '@mds/merck-design-system';
import type {MenuProps} from 'antd';
import {Flex, Button, Typography, Dropdown, Popover, Badge} from 'antd';
import {
  BellOutlined,
  CloseOutlined,
  DownOutlined,
  PlusOutlined,
  UserOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {useLocation} from 'react-router';
import {tid} from '../../../testUtils';

const TOUR_START_EVENT = 'mr-guided-tour:start';

export const NavBar: React.FC = () => {
  useLiveNotificationsUpdates();
  const {logout, auth} = useAuth();
  const dispatch = useDispatch();
  const user = auth.loggedInUser;
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const [allNotificationList, setAllNotificationList] = useState<NotificationDTO[]>();
  const {pathname, search} = useLocation();
  const isEditMode =
    pathname.startsWith(MRAppRoutes.MODEL_UPLOAD) && new URLSearchParams(search).get('editMode') === 'true';
  const navigate = useNavigate();
  const {openAboutDialog, AboutDialog} = useAboutDialog();
  const settings = useSettings();
  const documentation = settings?.documentation;
  const admin = isAdmin();
  const modelCreator = canCreateModel();
  const externalUserCreator = canCreateExternalUser();
  const tabLinks = useMemo(() => {
    return [
      ...(modelCreator ? [['Add Model Entry', MRAppRoutes.MODEL_UPLOAD]] : []),
      ['Model Entries', MRAppRoutes.MODEL_LIST],
      ['Execution Queue', MRAppRoutes.SCENARIO_EXECUTION_QUEUE],
      ...(externalUserCreator ? [['Externalization', MRAppRoutes.EXTERNALIZATION]] : []),
      // ...(admin ? [['Admin Space', MRAppRoutes.USER_LIST]] : []),
    ] as [string, MRAppRoutes][];
  }, [admin, modelCreator, externalUserCreator]);
  const notificationList = useNotifications(3);
  const {latestNotification, visited} = useSelector((state) => userNotificationsSelector(state));
  const helpLinks: [string, string?][] = useMemo(
    () => [
      ['Help & Training', documentation?.trainingSpaceUrl],
      ['Request Support', documentation?.supportUrl],
      ['Provide Feedback', documentation?.feedbackUrl],
      ['Release Notes', documentation?.releaseNotesUrl],
    ],
    [documentation],
  );

  const helpMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    items.push({
      key: 'guided-tour',
      label: 'Guided Tour of MR',
      ...tid('help-menu-item', 'Guided Tour of MR'),
    });

    // Add help links
    helpLinks.forEach(([title, url], index) => {
      if (url) {
        items.push({
          key: `help-${index}`,
          label: (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {title}
            </a>
          ),
        });
      }
    });

    // Add Privacy Policy
    if (settings?.privacyPolicyUrl) {
      items.push({
        key: 'privacy-policy',
        label: (
          <a href={settings.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" style={{color: 'inherit'}}>
            Privacy Policy
          </a>
        ),
        ...tid('help-menu-item', 'Privacy policy'),
      });
    }

    // Add About
    items.push({
      key: 'about',
      label: 'About',
      ...tid('help-menu-item', 'About'),
    });

    return items;
  }, [helpLinks, openAboutDialog, settings?.privacyPolicyUrl, user?.userType]);

  const userMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'logout',
        label: (
          <Button style={{width: '100%'}} type="link">
            Logout
          </Button>
        ),
        onClick: logout,
      },
    ],
    [logout],
  );

  const handleHelpMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(
    ({key}) => {
      if (key === 'guided-tour') {
        window.dispatchEvent(new Event(TOUR_START_EVENT));
        return;
      }

      if (key === 'about') {
        openAboutDialog({});
      }
    },
    [openAboutDialog],
  );

  // const handleUserMenuToggleClick = (event: MouseEvent) => {
  //   setUserMenuAnchor(event.currentTarget);
  // };

  // const handleHelpMenuToggleClick = (event: MouseEvent) => {
  //   setHelpMenuAnchor(event.currentTarget);
  // };

  // const handleTourClick = useCallback(() => {
  //   window.location.replace(getOrigin() + '/#/model');
  //   setTimeout(() => {
  //     setShowTour(true);
  //   }, 1000);
  // }, []);

  // const handleTourComplete = () => {
  //   setShowTour(false);
  // };

  const activeTabIndex = useMemo(() => {
    // Admin pages
    if (pathname.startsWith('/admin/')) {
      return 4;
    }

    // In edit mode, keep 'Model Entries' highlighted
    if (isEditMode) {
      return tabLinks.findIndex(([, url]) => url === MRAppRoutes.MODEL_LIST);
    }

    // Check for hash-based routing (React Router compatibility)
    const currentHash = window.location.hash.replace('#', '');
    const currentPath = pathname || currentHash;

    const matchedIndex = tabLinks.findIndex(([, url]) => {
      return currentPath.startsWith(url) || pathname.startsWith(url);
    });

    return matchedIndex;
  }, [pathname, tabLinks, isEditMode]);

  useEffect(() => {
    if (!allNotificationList) {
      requestLatestNotifications()
        .then((response) => {
          setAllNotificationList(response);
        })
        .catch(() => {
          dispatch(messageAdd('Failed to load the notification list', MessageTypes.ERROR));
        });
    }
  }, [dispatch, allNotificationList]);

  const handleShowAllButtonClick = useCallback(() => {
    setIsNotificationMenuOpen(false);
    navigate(getUrlRoute(MRAppRoutes.ALL_NOTIFICATIONS));
  }, [navigate]);

  const handleClearAll = useCallback(() => {
    const inActivateIds = (notificationList || []).map((item) => item.notificationId);
    if (inActivateIds.length > 0) {
      deleteNotificationAction(dispatch, inActivateIds);
    }
  }, [dispatch, notificationList]);

  const handleItemClose = useCallback(
    (notificationId: number) => {
      deleteNotificationAction(dispatch, [notificationId]);
    },
    [dispatch],
  );

  const handleItemText = useCallback((item: NotificationDTO) => {
    if (item.notificationAction === 'NEW_MODEL_VERSION') {
      return 'has added a new model version';
    } else {
      return 'has modified files in the model';
    }
  }, []);

  const handleItemLink = useCallback((item: NotificationDTO) => {
    if (item.notificationAction === 'NEW_MODEL_VERSION') {
      return `${window.location.origin}/#${getUrlRoute(MRAppRoutes.MODEL_DETAIL, {
        uuid: item.modelUuid,
      })}`;
    } else {
      return `${window.location.origin}/#${getUrlRoute(MRAppRoutes.MODEL_DETAIL, {
        uuid: item.modelUuid,
      })}?tab=folder`;
    }
  }, []);

  const currentPage = window.location.hash;

  const notificationPopoverContent = (
    <div className={styles.notificationsMenu}>
      <div className={styles.notificationsHeader}>
        <span>Notifications</span>
        {notificationList && notificationList.length > 0 ? (
          <span className={styles.clearAll} onClick={handleClearAll}>
            Clear All
          </span>
        ) : (
          <span className={styles.rightElement}>Clear All</span>
        )}
      </div>
      <div className={styles.notificationsContainer}>
        {notificationList && notificationList.length > 0 ? (
          notificationList.map((item: NotificationDTO) => (
            <div key={item.notificationId} className={styles.notificationItem}>
              <div className={styles.leftPane}>
                <div className={styles.notificationTitle}>
                  {item.createdBy} {handleItemText(item)}
                </div>
                <div className={styles.notificationText}>{item.userNote}</div>
                <div className={styles.notificationLink} onClick={() => setIsNotificationMenuOpen(false)}>
                  <a href={handleItemLink(item)}>Click here to review</a>
                </div>
                <div className={styles.notificationMeta}>
                  {item.createdOn.toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS)}
                </div>
              </div>
              <div className={styles.rightPane}>
                <Button
                  type="text"
                  className={styles.closeButton}
                  icon={<CloseOutlined />}
                  onClick={() => handleItemClose(item.notificationId)}
                />
              </div>
            </div>
          ))
        ) : (
          <Typography className={styles.notificationsFooter}>No new notifications.</Typography>
        )}
      </div>
      {allNotificationList ? (
        <span className={styles.showAllButton} onClick={handleShowAllButtonClick}>
          Show All Notifications
        </span>
      ) : (
        <span className={styles.showAllGreyButton}>Show All Notifications</span>
      )}
    </div>
  );

  return (
    <>
      <Header
        title={
          (
            <Typography.Text
              onClick={() => navigate(MRAppRoutes.ROOT)}
              className={styles.headerTitle}
              style={{
                color: 'white',
                fontSize: '20px',
                fontWeight: 700,
                width: 'max-content',
                cursor: 'pointer',
              }}
            >
              Model Repository
            </Typography.Text>
          ) as unknown as string
        }
        dark
        Logo={<Logo icon />}
      >
        <Flex
          justify="space-between"
          align="center"
          style={{width: '100%', height: '100%'}}
          className={styles.headerFlex}
        >
          <Flex style={{height: '100%'}} align="center" className={styles.leftNav}>
            {tabLinks.map(([label, url], index) => (
              <Button
                type="link"
                key={url}
                onClick={() => navigate(url)}
                icon={label === 'Add Model Entry' ? <PlusOutlined /> : null}
                className={styles.navTabButton}
                data-tour-id={label === 'Model Entries' ? 'nav-model-entries' : undefined}
                style={{
                  height: '100%',
                  backgroundColor: activeTabIndex === index || currentPage === `#${url}` ? '#339D96' : 'transparent',
                  color:
                    activeTabIndex === index || currentPage === `#${url}` ? '#FFFFFF' : 'rgba(255, 255, 255, 0.65)',
                  fontWeight: 400,
                }}
              >
                <span data-tour-id={label === 'Add Model Entry' ? 'nav-add-model-entry' : undefined}>{label}</span>
              </Button>
            ))}
          </Flex>
          <Flex style={{height: '100%'}} align="center" className={styles.rightNav}>
            {admin && (
              <Button
                type="link"
                onClick={() => navigate(MRAppRoutes.USER_LIST)}
                icon={<UserOutlined />}
                className={styles.navTabButton}
                style={{
                  height: '100%',
                  backgroundColor: pathname.startsWith('/admin/') ? '#339D96' : 'transparent',
                  color: pathname.startsWith('/admin/') ? '#FFFFFF' : 'rgba(255, 255, 255, 0.65)',
                  fontWeight: 400,
                }}
              >
                Admin Space
              </Button>
            )}
            <>
              <Popover
                trigger="click"
                open={isNotificationMenuOpen}
                onOpenChange={(open) => {
                  setIsNotificationMenuOpen(open);
                  if (open) {
                    dispatch(resetLatestNotificationFiled());
                  }
                }}
                placement="bottomRight"
                arrow={false}
                overlayClassName={styles.notificationsPopover}
                content={notificationPopoverContent}
              >
                <Button type="link" style={{height: '100%'}}>
                  <Badge dot={!!(latestNotification || !visited)} offset={[-2, 6]}>
                    <BellOutlined style={{color: 'rgba(255, 255, 255, 0.65)', fontSize: '19px'}} />
                  </Badge>
                </Button>
              </Popover>
            </>
            {user && (
              <>
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: userMenuItems,
                  }}
                >
                  <Button
                    type="link"
                    className={styles.iconOnlyButton}
                    style={{
                      height: '100%',
                      color: 'rgba(255, 255, 255, 0.65)',
                      fontWeight: 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <UserOutlined />
                    {fullNameWestern(user)}
                    <DownOutlined />
                  </Button>
                </Dropdown>

                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: helpMenuItems,
                    onClick: handleHelpMenuClick,
                  }}
                >
                  <Button
                    type="link"
                    className={styles.iconOnlyButton}
                    data-tour-id="help-button"
                    style={{
                      height: '100%',
                      color: 'rgba(255, 255, 255, 0.65)',
                      fontWeight: 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <QuestionCircleOutlined />
                    Help
                    <DownOutlined />
                  </Button>
                </Dropdown>
              </>
            )}
          </Flex>
        </Flex>
      </Header>
      {AboutDialog}
    </>
  );
};

/*
  TBD:
  - need to rework the help menu
  - need to rework guided tour
  - need to rework notifications
*/

// <>
//   <Tour showTour={showTour} onTourComplete={handleTourComplete} userType={user?.userType} />
//   <Typography variant="h6">
//     <Link
//       component={RouterLink}
//       to={MRAppRoutes.MODEL_LIST}
//       color="inherit"
//       className={styles.title}
//       {...tid('logo')}
//     >
//       <IconLogo className={styles.logo} />
//       <strong>Model Repository</strong>
//     </Link>
//   </Typography>
//   <Tabs
//     value={activeTabIndex !== -1 ? activeTabIndex : false}
//     classes={{indicator: styles.indicator}}
//     indicatorColor="primary"
//     textColor="inherit"
//     variant="fullWidth"
//   >
//     {tabLinks.map(([label, url]) => (
//       <Tab
//         sx={{minWidth: 200}}
//         {...tid('navbar', ...label.toLocaleLowerCase().split(' '))}
//         key={url}
//         label={label}
//         component={RouterLink}
//         to={url}
//         classes={{root: styles.navButton}}
//       />
//     ))}
//   </Tabs>
//   <div>
//     <>
//       <Button
//         {...tid('notification-menu-button')}
//         aria-controls="user-menu"
//         aria-haspopup="true"
//         onClick={handleNotificationMenuToggleClick}
//         className={!!notificationMenuAnchor ? styles.notificationsButtonSelected : styles.notificationsButton}
//       >
//         <div>
//           <NotificationsOutlinedIcon />
//           {latestNotification || !visited ? <span className={styles.notificationIdentifier}></span> : null}
//         </div>
//       </Button>
//       <Popover
//         id="notification-menu"
//         open={!!notificationMenuAnchor}
//         anchorEl={notificationMenuAnchor}
//         onClose={() => setNotificationMenuAnchor(null)}
//         anchorOrigin={{
//           vertical: 'bottom',
//           horizontal: 'right',
//         }}
//         transformOrigin={{
//           vertical: 'top',
//           horizontal: 'right',
//         }}
//       >
//         {notificationList && (
//           <div className={styles.notificationsMenu}>
//             <div className={styles.notificationsHeader}>
//               <span>Notifications</span>
//               {notificationList.length > 0 ? (
//                 <span className={styles.clearAll} onClick={handleClearAll}>
//                   Clear All
//                 </span>
//               ) : (
//                 <span className={styles.rightElement}>Clear All</span>
//               )}
//             </div>
//             <div className={notificationList.length > 0 ? '' : styles.notificationsContainer}>
//               {notificationList.map((item: NotificationDTO) => {
//                 return (
//                   <div key={item.notificationId} className={styles.notificationItem}>
//                     <div className={styles.leftPane}>
//                       <>
//                         <div style={{fontWeight: 'bold'}}>
//                           {item.createdBy} {handleItemText(item)}{' '}
//                         </div>
//                         <div>{item.userNote}</div>
//                         <div style={{marginBottom: '20px'}} onClick={() => setNotificationMenuAnchor(null)}>
//                           <a style={{color: '#fff'}} href={handleItemLink(item)}>
//                             Click here to review
//                           </a>
//                         </div>
//                       </>
//                       <div>{item.createdOn.toLocaleDateString('en-US', TO_LOCALE_DATE_STRING_OPTIONS)}</div>
//                     </div>
//                     <div className={styles.rightPane}>
//                       <IconButton
//                         className={styles.closeButton}
//                         data-item={item.notificationId}
//                         onClick={handleItemClose}
//                       >
//                         <CloseIcon />
//                       </IconButton>
//                     </div>
//                   </div>
//                 );
//               })}
//             </div>
//             {!notificationList.length && (
//               <Typography className={styles.notificationsFooter}>No new notifications.</Typography>
//             )}
//             {allNotificationList ? (
//               <span className={styles.showAllButton} onClick={handleShowAllButtonClick}>
//                 Show all notifications
//               </span>
//             ) : (
//               <span className={styles.showAllGreyButton}>Show all notifications</span>
//             )}
//           </div>
//         )}
//       </Popover>
//     </>
//     {user && (
//       <>
//         <Button
//           {...tid('user-menu-button')}
//           aria-controls="user-menu"
//           aria-haspopup="true"
//           onClick={handleUserMenuToggleClick}
//           className={styles.userMenuButton}
//         >
//           {fullNameWestern(user)} <ArrowDropDownIcon />
//         </Button>
//         <Menu
//           id="user-menu"
//           anchorEl={userMenuAnchor}
//           keepMounted
//           open={!!userMenuAnchor}
//           onClose={() => setUserMenuAnchor(null)}
//         >
//           <MenuItem dense={true} onClick={logout}>
//             <TextOrProgress active={auth.isFetching}>Logout</TextOrProgress>
//           </MenuItem>
//         </Menu>
//       </>
//     )}
//     <Button
//       {...tid('help-menu-button')}
//       aria-controls="help-menu"
//       aria-haspopup="true"
//       onClick={handleHelpMenuToggleClick}
//       className={styles.helpMenuButton}
//     >
//       Help <ArrowDropDownIcon />
//     </Button>
//     <Menu
//       id="help-menu"
//       anchorEl={helpMenuAnchor}
//       keepMounted
//       open={!!helpMenuAnchor}
//       onClose={() => setHelpMenuAnchor(null)}
//     >
//       {user?.userType !== 'EXTERNAL_USER' && (
//         <MenuItem
//           dense={true}
//           {...tid('help-menu-item', 'Guided Tour of MR ')}
//           onClick={() => {
//             setHelpMenuAnchor(null);
//             handleTourClick();
//           }}
//         >
//           Guided Tour of MR
//         </MenuItem>
//       )}
//       {helpLinks.map(([title, url]) => (
//         <MenuItem key={url} dense={true} component="a" href={url} target="_blank" {...tid('help-menu-item', title)}>
//           {title}
//         </MenuItem>
//       ))}
//       <MenuItem
//         dense={true}
//         component="a"
//         href={settings?.privacyPolicyUrl}
//         target="_blank"
//         {...tid('help-menu-item', 'Privacy policy')}
//       >
//         Privacy policy
//       </MenuItem>
//       <MenuItem
//         {...tid('help-menu-item', 'About')}
//         dense={true}
//         onClick={() => {
//           setHelpMenuAnchor(null);
//           openAboutDialog({});
//         }}
//       >
//         About
//       </MenuItem>
//     </Menu>
//   </div>
//   {AboutDialog}
// </>
